import { useEffect, useMemo, useState } from 'react';
import { useApiClientsOrNull } from '../context/ApiClientsContext';
import { getContext } from '../api/contextApi';
import { getLocalizedText } from '../utils/getLocalizedText';

export interface ContextMetadata {
  systemName: string;
  displayName: string;
  description: string | null;
}

export interface UseContextMetadataResult {
  /** Map keyed by contextSystemName → metadata. Populated incrementally. */
  resolved: Map<string, ContextMetadata>;
  loading: boolean;
}

/**
 * Format a context system_name into a friendly fallback label.
 * Used when the context can't be resolved (404, network error, in-flight).
 */
function formatSystemName(systemName: string): string {
  return systemName
    .replaceAll('_', ' ')
    .replaceAll(/\b\w/g, (c) => c.toUpperCase());
}

/**
 * Module-scope cache. Contexts are tenant-scoped, so we key by
 * `${schemaClientRef}::${systemName}` — different impersonation/auth
 * contexts won't share cached entries.
 *
 * We hold the schemaClient as the cache scope rather than organizationId
 * because `getContext()` doesn't take an organizationId parameter (the
 * route resolves tenant from auth context).
 */
const moduleCache = new WeakMap<object, Map<string, ContextMetadata>>();

function getCacheBucket(client: object): Map<string, ContextMetadata> {
  let bucket = moduleCache.get(client);
  if (!bucket) {
    bucket = new Map();
    moduleCache.set(client, bucket);
  }
  return bucket;
}

/**
 * Resolve a list of context system_names into `{ displayName }` so the
 * marketplace's "Contexts" bucket can render friendly names instead of
 * leaking raw `xxx_yyy_zzz` strings to users.
 *
 * Mirrors `useSchemaMetadata`: incrementally consistent (always returns
 * a Map containing a fallback entry per requested name; upgrades entries
 * once the schema-service responds).
 */
export function useContextMetadata(
  systemNames: string[] | null | undefined,
): UseContextMetadataResult {
  const apiClients = useApiClientsOrNull();
  const schemaClient = apiClients?.schemaClient;

  const uniqueNames = useMemo(() => {
    if (!systemNames || systemNames.length === 0) return [] as string[];
    const set = new Set(systemNames.filter(Boolean));
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [systemNames]);

  const namesKey = uniqueNames.join(',');

  const [resolved, setResolved] = useState<Map<string, ContextMetadata>>(() => {
    const initial = new Map<string, ContextMetadata>();
    const bucket = schemaClient ? getCacheBucket(schemaClient) : null;
    for (const name of uniqueNames) {
      const cached = bucket?.get(name);
      initial.set(
        name,
        cached ?? {
          systemName: name,
          displayName: formatSystemName(name),
          description: null,
        },
      );
    }
    return initial;
  });

  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (uniqueNames.length === 0) {
      setResolved(new Map());
      setLoading(false);
      return;
    }

    setResolved((prev) => {
      const next = new Map(prev);
      const bucket = schemaClient ? getCacheBucket(schemaClient) : null;
      for (const name of uniqueNames) {
        const cached = bucket?.get(name);
        if (cached) {
          next.set(name, cached);
        } else if (!next.has(name)) {
          next.set(name, {
            systemName: name,
            displayName: formatSystemName(name),
            description: null,
          });
        }
      }
      return next;
    });

    if (!schemaClient) return;

    const bucket = getCacheBucket(schemaClient);
    const namesToFetch = uniqueNames.filter((name) => !bucket.has(name));
    if (namesToFetch.length === 0) return;

    let cancelled = false;
    setLoading(true);

    const run = async () => {
      const fetchedEntries: Array<[string, ContextMetadata]> = [];

      for (const systemName of namesToFetch) {
        try {
          const context = await getContext(schemaClient, systemName);
          const displayName =
            getLocalizedText(context?.name) || formatSystemName(systemName);
          const description = context?.description
            ? getLocalizedText(context.description)
            : null;
          const metadata: ContextMetadata = {
            systemName,
            displayName,
            description: description || null,
          };
          bucket.set(systemName, metadata);
          fetchedEntries.push([systemName, metadata]);
        } catch {
          // Leave the fallback in place; do not cache failures so retries can recover.
        }
      }

      if (!cancelled && fetchedEntries.length > 0) {
        setResolved((prev) => {
          const next = new Map(prev);
          for (const [name, meta] of fetchedEntries) {
            next.set(name, meta);
          }
          return next;
        });
      }

      if (!cancelled) {
        setLoading(false);
      }
    };

    run();

    return () => {
      cancelled = true;
    };
  }, [schemaClient, namesKey, uniqueNames]);

  return { resolved, loading };
}

/**
 * Test-only helper for clearing the module cache between test cases.
 */
export function _resetContextMetadataCacheForTests(): void {
  // WeakMap can't iterate; a fresh test hook caller will get a new bucket
  // when no entry exists for its client reference. This function is a no-op
  // placeholder that keeps the API surface symmetric with useSchemaMetadata.
}
